import React, { useCallback, useState } from 'react';
import { ApplicationType } from '@/contexts/dynamic-config';
import { ItemListOption } from '@/contexts/item-context-types';
import { MultiOrderGuideButton } from '@/components/orders/multi-order-guide-button';
import { OrderGuidePartitionSectionActionSheet } from '@/components/order-guide/order-guide-partition-section-sheet';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';

interface UseMultiOrderGuideButtonReturn {
  renderOrderGuideButton: (variantPackUUID: string) => React.ReactNode;
  renderPartitionSelectorSheet: () => React.ReactNode;
  shouldShowOrderGuideButton: (option: ItemListOption) => boolean;
}

/**
 * Custom hook to manage multi-order guide button state and rendering logic.
 * Encapsulates the common functionality for showing order guide buttons and
 * handling partition selector interactions.
 *
 * @remarks
 * Button visibility logic:
 * - When custom order guide editing is enabled:
 *   - Always show button for all items (single and multi-SKU)
 *   - Allows users to add any item to their order guide
 * - When custom order guide editing is disabled:
 *   - Show button only if item is already in order guide
 */
export const useMultiOrderGuideButton = (): UseMultiOrderGuideButtonReturn => {
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { isInOrderGuide } = useOrderGuideItemsContext();
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;

  const [
    partitionSelectorVariantPackUUID,
    setPartitionSelectorVariantPackUUID,
  ] = useState<string | null>(null);

  const renderOrderGuideButton = useCallback((variantPackUUID: string) => {
    return (
      <MultiOrderGuideButton
        openPartitionSelector={() => {
          setPartitionSelectorVariantPackUUID(variantPackUUID);
        }}
        variantPackUUID={variantPackUUID}
      />
    );
  }, []);

  const shouldShowOrderGuideButton = useCallback(
    (option: ItemListOption) => {
      if (customOrderGuideEditingEnabled) {
        return true;
      }

      // Show if at least one variant pack is already in the order guide
      return (
        !isCatalogLimited &&
        (option.metadata?.skus.some((sku) =>
          isInOrderGuide(sku.variantPackUUID),
        ) ??
          false)
      );
    },
    [customOrderGuideEditingEnabled, isCatalogLimited, isInOrderGuide],
  );

  const renderPartitionSelectorSheet = useCallback(() => {
    return partitionSelectorVariantPackUUID ? (
      <OrderGuidePartitionSectionActionSheet
        onClose={() => setPartitionSelectorVariantPackUUID(null)}
        variantPackUUID={partitionSelectorVariantPackUUID}
      />
    ) : null;
  }, [partitionSelectorVariantPackUUID]);

  return {
    renderOrderGuideButton,
    renderPartitionSelectorSheet,
    shouldShowOrderGuideButton,
  };
};
